import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { NavLink } from 'react-router-dom';
import { functions } from '../../api/firebase';

// Design system components
import '../../design-system/tokens/index.css';
import {
  DashboardLayout,
  ResultsGrid,
  ContactCard,
  ContactCardSkeleton,
  SearchInput,
  FilterSection,
  FilterCheckboxGroup,
  FilterTagGroup,
  ActiveFilters,
  FilterSidebar,
  Button,
  EmptyCard,
} from '../../design-system';
import { Countries, TargetTags, Beats } from '../../data/selectOptions';

import Loading from '../misc/Loading';
import NoAuth from '../misc/noAuth';
import AddAllToListButton from '../search/addAllToListButton';
import AddToListButton from '../search/addToListButton';
const {
  canRunJournalistSearch,
} = require('./journalistSearchState');

// Icons
const UsersIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

// Filter options come from shared selectOptions to match stored values.

/**
 * JournalistDashboard - Modern journalist database search
 * Uses the Press Hunt 2.0 design system
 */
function JournalistDashboard({ auth, subscription, plan, status, lists }) {
  // State
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState({
    country: '',
    area: '',
    industry: [],
    tags: [],
    outlet_text: [],
    has_email: null,
    has_phone_number: null,
    kind_of_journalist: '',
  });
  const [pageSize, setPageSize] = useState(20);
  const [pageSizeOpen, setPageSizeOpen] = useState(false);
  const [pageSizeMenuPos, setPageSizeMenuPos] = useState(null);
  const [cursorHistory, setCursorHistory] = useState([null]);
  const [cursorIndex, setCursorIndex] = useState(0);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [hits, setHits] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);

  // Refs
  const searchTimeout = useRef(null);
  const searchRequestId = useRef(0);
  const pageSizeRef = useRef(null);
  const searchJournalists = useRef(functions.httpsCallable('searchJournalists'));
  const searchJournalistsPreview = useRef(functions.httpsCallable('searchJournalistsPreview'));

  // Determine effective page size based on subscription
  const getEffectivePageSize = useCallback(() => {
    if (!auth || !subscription || status !== 'active') {
      return 3;
    }
    return pageSize;
  }, [auth, subscription, status, pageSize]);

  // Check if user has full access
  const hasFullAccess = auth && subscription && status === 'active' &&
    subscription.searchEntitlementVerified === true;
  const hasPagination = [2, 20, 3, 30, 4, 40, 5, 50, 6, 60, 7, 70].includes(plan);

  // Fetch one bounded cursor page.
  const fetchResults = useCallback(async (overrides = {}) => {
    const effectivePageSize = overrides.pageSize || getEffectivePageSize();
    const nextQuery = overrides.query !== undefined ? overrides.query : query;
    const nextFilters = overrides.filters !== undefined ? overrides.filters : filters;
    const cursor = overrides.cursor !== undefined
      ? overrides.cursor
      : cursorHistory[cursorIndex];
    const canSearch = canRunJournalistSearch({
      query: nextQuery,
      filters: nextFilters,
      hasFullAccess,
    });
    const requestId = searchRequestId.current + 1;
    searchRequestId.current = requestId;
    setError(null);

    if (!canSearch) {
      setHits([]);
      setHasMore(false);
      setNextCursor(null);
      setHasSearched(false);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    setHasSearched(true);

    try {
      const callable = hasFullAccess
        ? searchJournalists.current
        : searchJournalistsPreview.current;
      const payload = hasFullAccess
        ? {
          query: nextQuery,
          filters: nextFilters,
          cursor,
          pageSize: effectivePageSize,
        }
        : { query: nextQuery };
      const response = await callable(payload);
      if (requestId !== searchRequestId.current) return;

      const {
        hits: responseHits = [],
        hasMore: responseHasMore = false,
        nextCursor: responseNextCursor = null,
      } = response.data || {};
      const normalizedHits = responseHits.map((hit) => ({
        ...(hit.doc || {}),
        ...hit,
        objectID: hit.id || hit.objectID,
      }));

      setHits(normalizedHits);
      setHasMore(Boolean(responseHasMore));
      setNextCursor(responseNextCursor);
    } catch (err) {
      if (requestId !== searchRequestId.current) return;
      console.error('Journalist search failed', err);
      setHits([]);
      setHasMore(false);
      setNextCursor(null);
      if (err && err.code === 'functions/resource-exhausted') {
        setError('Too many searches. Please wait a moment and try again.');
      } else if (err && err.code === 'functions/permission-denied') {
        setError('An active subscription is required for full journalist search.');
      } else {
        setError('Search failed. Please try again.');
      }
    } finally {
      if (requestId === searchRequestId.current) {
        setIsSearching(false);
      }
    }
  }, [
    cursorHistory,
    cursorIndex,
    filters,
    getEffectivePageSize,
    hasFullAccess,
    query,
  ]);

  // Debounced search
  const enqueueSearch = useCallback((overrides = {}) => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => fetchResults(overrides), 300);
  }, [fetchResults]);

  const resetAndEnqueueSearch = useCallback((overrides = {}) => {
    setCursorHistory([null]);
    setCursorIndex(0);
    setNextCursor(null);
    setHasMore(false);
    enqueueSearch({ ...overrides, cursor: null });
  }, [enqueueSearch]);

  const updatePageSizeMenuPos = useCallback(() => {
    if (!pageSizeRef.current) return;
    const rect = pageSizeRef.current.getBoundingClientRect();
    const width = 176;
    const left = Math.max(12, rect.right - width);
    const top = rect.bottom + 6;
    setPageSizeMenuPos({ top, left, width });
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pageSizeRef.current && !pageSizeRef.current.contains(event.target)) {
        setPageSizeOpen(false);
      }
    };
    const handleWindowChange = () => {
      if (pageSizeOpen) updatePageSizeMenuPos();
    };
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleWindowChange, true);
    window.addEventListener('resize', handleWindowChange);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleWindowChange, true);
      window.removeEventListener('resize', handleWindowChange);
    };
  }, [pageSizeOpen, updatePageSizeMenuPos]);

  // Handle query change
  const handleQueryChange = (e) => {
    const nextQuery = e.target.value;
    setQuery(nextQuery);
    resetAndEnqueueSearch({ query: nextQuery });
  };

  const updateFiltersAndSearch = (nextFilters) => {
    setFilters(nextFilters);
    resetAndEnqueueSearch({ filters: nextFilters });
  };

  // Handle filter changes
  const handleBeatsChange = (newBeats) => {
    updateFiltersAndSearch({ ...filters, industry: newBeats });
  };

  const handleTopicsChange = (newTopics) => {
    updateFiltersAndSearch({ ...filters, tags: newTopics });
  };

  const handleCountryChange = (newCountries) => {
    updateFiltersAndSearch({ ...filters, country: newCountries[0] || '' });
  };

  const handleHasEmailChange = (checked) => {
    updateFiltersAndSearch({ ...filters, has_email: checked ? true : null });
  };

  const handleHasPhoneChange = (checked) => {
    updateFiltersAndSearch({ ...filters, has_phone_number: checked ? true : null });
  };

  const handleNextPage = () => {
    if (!hasMore || !nextCursor) return;
    const nextIndex = cursorIndex + 1;
    setCursorHistory((current) => [
      ...current.slice(0, nextIndex),
      nextCursor,
    ]);
    setCursorIndex(nextIndex);
    fetchResults({ cursor: nextCursor });
  };

  const handlePreviousPage = () => {
    if (cursorIndex === 0) return;
    const previousIndex = cursorIndex - 1;
    setCursorIndex(previousIndex);
    fetchResults({ cursor: cursorHistory[previousIndex] });
  };

  // Clear all filters
  const handleClearFilters = () => {
    const nextFilters = {
      country: '',
      area: '',
      industry: [],
      tags: [],
      outlet_text: [],
      has_email: null,
      has_phone_number: null,
      kind_of_journalist: '',
    };
    setFilters(nextFilters);
    setQuery('');
    resetAndEnqueueSearch({ query: '', filters: nextFilters });
  };

  // Build active filters list
  const activeFilters = [
    ...filters.industry.map((b) => ({ type: 'beat', value: b, label: b })),
    ...filters.tags.map((t) => ({ type: 'topic', value: t, label: t })),
    ...(filters.country ? [{ type: 'country', value: filters.country, label: filters.country }] : []),
    ...(filters.has_email ? [{ type: 'has_email', value: true, label: 'Has email' }] : []),
    ...(filters.has_phone_number ? [{ type: 'has_phone', value: true, label: 'Has phone' }] : []),
  ];

  const handleRemoveFilter = (filter) => {
    let nextFilters = filters;
    switch (filter.type) {
      case 'beat':
        nextFilters = {
          ...filters,
          industry: filters.industry.filter((beat) => beat !== filter.value),
        };
        break;
      case 'topic':
        nextFilters = {
          ...filters,
          tags: filters.tags.filter((tag) => tag !== filter.value),
        };
        break;
      case 'country':
        nextFilters = { ...filters, country: '' };
        break;
      case 'has_email':
        nextFilters = { ...filters, has_email: null };
        break;
      case 'has_phone':
        nextFilters = { ...filters, has_phone_number: null };
        break;
      default:
        return;
    }
    updateFiltersAndSearch(nextFilters);
  };

  // Transform hit to contact format for ContactCard
  const hitToContact = (hit) => ({
    name: hit.name || 'Unknown',
    avatar: hit.avatar ? hit.avatar.replace('_normal', '') : null,
    title: hit.kind_of_journalist || null,
    outlet: hit.outlet_text && hit.outlet_text[0] ? hit.outlet_text[0] : null,
    outlets: hit.outlet_text || [],
    bio: hit.twitter_bio || null,
    beats: hit.industry || [],
    topics: hit.tags || [],
    email: hit.email || null,
    phone: hit.phone || null,
    twitter: hit.twitter_name || null,
    linkedin: hit.linkedin_url || null,
    website: hit.journalist_url || null,
    country: hit.country || null,
    countryFlag: hit.country_flag_url || null,
  });

  // Not authenticated
  if (!auth) {
    return <NoAuth />;
  }

  // Loading subscription
  if (subscription && subscription.tempStripePaymentTokenId) {
    return <Loading />;
  }


  // Filter sidebar content
  const filterSidebar = (
    <FilterSidebar onReset={handleClearFilters}>
      <ActiveFilters
        filters={activeFilters}
        onRemove={handleRemoveFilter}
        onClearAll={handleClearFilters}
      />

      <FilterSection title="Contact Info" defaultOpen={true}>
        <div className="space-y-2">
          <label className="flex items-center gap-3 py-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.has_email || false}
              onChange={(e) => handleHasEmailChange(e.target.checked)}
              className="w-4 h-4 rounded border-[var(--color-border-strong)] text-[var(--color-primary-500)] focus:ring-[var(--color-primary-500)]"
            />
            <span className="text-sm text-[var(--color-text-secondary)]">Has email address</span>
          </label>
          <label className="flex items-center gap-3 py-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.has_phone_number || false}
              onChange={(e) => handleHasPhoneChange(e.target.checked)}
              className="w-4 h-4 rounded border-[var(--color-border-strong)] text-[var(--color-primary-500)] focus:ring-[var(--color-primary-500)]"
            />
            <span className="text-sm text-[var(--color-text-secondary)]">Has phone number</span>
          </label>
        </div>
      </FilterSection>

      <FilterSection title="Country" badge={filters.country ? 1 : null}>
        <FilterTagGroup
          options={Countries}
          selected={filters.country ? [filters.country] : []}
          onChange={handleCountryChange}
        />
      </FilterSection>

      <FilterSection title="Beats" badge={filters.industry.length || null}>
        <FilterCheckboxGroup
          options={Beats}
          selected={filters.industry}
          onChange={handleBeatsChange}
          searchable
          searchPlaceholder="Search beats..."
          maxVisible={6}
        />
      </FilterSection>

      <FilterSection title="Topics" badge={filters.tags.length || null}>
        <FilterCheckboxGroup
          options={TargetTags}
          selected={filters.tags}
          onChange={handleTopicsChange}
          searchable
          searchPlaceholder="Search topics..."
          maxVisible={6}
        />
      </FilterSection>

      {!hasFullAccess && (
        <div className="mt-6 p-4 bg-[var(--color-primary-50)] rounded-[var(--radius-lg)] border border-[var(--color-primary-200)]">
          <h4 className="font-semibold text-[var(--color-primary-700)] mb-2">Unlock All Filters</h4>
          <p className="text-sm text-[var(--color-primary-600)] mb-3">
            Upgrade to access advanced filters and see all contact details.
          </p>
          <Button as={NavLink} to="/pricing" variant="primary" size="sm" fullWidth>
            View Plans
          </Button>
        </div>
      )}
    </FilterSidebar>
  );

  // Header actions
  const headerActions = (
    <div className="flex items-center gap-3">
      {hasFullAccess && (
        <div ref={pageSizeRef} className="relative">
          <button
            type="button"
            onClick={() => {
              setPageSizeOpen((prev) => {
                const next = !prev;
                if (next) updatePageSizeMenuPos();
                return next;
              });
            }}
            className="inline-flex items-center gap-2 h-9 px-3 text-sm bg-white border border-gray-200 rounded-lg text-gray-700 shadow-sm hover:border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {pageSize} contacts
            <svg className={`h-4 w-4 text-gray-400 transition-transform ${pageSizeOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {pageSizeOpen && pageSizeMenuPos && ReactDOM.createPortal(
            <div
              className="bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden"
              style={{
                position: 'fixed',
                top: pageSizeMenuPos.top,
                left: pageSizeMenuPos.left,
                width: pageSizeMenuPos.width,
                zIndex: 2000,
              }}
            >
              {[20, 50].map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => {
                    setPageSizeOpen(false);
                    setPageSize(value);
                    resetAndEnqueueSearch({ pageSize: value });
                  }}
                  className="flex items-center gap-2 w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
                >
                  {pageSize === value ? (
                    <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <span className="w-4 h-4" />
                  )}
                  {value} contacts
                </button>
              ))}
            </div>,
            document.body
          )}
        </div>
      )}

      {hasPagination ? (
        <AddAllToListButton profiles={hits} dataType="journalist" />
      ) : (
        <Button as={NavLink} to="/pricing" variant="primary">
          Save page
        </Button>
      )}
    </div>
  );

  return (
    <DashboardLayout
      title="Journalist Database"
      subtitle="Find journalists by name, outlet, beat, or location"
      sidebar={filterSidebar}
      header={headerActions}
      activeFilterCount={activeFilters.length}
    >
      <div className="mb-6">
        <SearchInput
          value={query}
          onChange={handleQueryChange}
          onClear={() => {
            setQuery('');
            resetAndEnqueueSearch({ query: '' });
          }}
          placeholder="Search journalists by name, outlet, or topic..."
          size="md"
          className="w-full"
        />
        <div className="mt-2 text-right text-sm text-[var(--color-muted-foreground)]">
          Looking for investors too?{' '}
          <a
            href="https://investorscout.co/"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline"
            style={{ color: '#22c55e' }}
          >
            Try Investor Scout.
          </a>
        </div>
      </div>

      {hasSearched && !isSearching && (
        <div className="mb-6 pb-5 border-b border-gray-200 text-[15px] text-gray-600">
          Showing <span className="font-semibold text-gray-900">{hits.length}</span>
          {hasMore ? ' results on this page. More are available.' : ' results.'}
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-[var(--color-error-50)] border border-[var(--color-error-200)] rounded-[var(--radius-lg)] text-[var(--color-error-600)]">
          {error}
        </div>
      )}

      {isSearching ? (
        <ResultsGrid>
          {[1, 2, 3].map((i) => (
            <ContactCardSkeleton key={i} />
          ))}
        </ResultsGrid>
      ) : hits.length === 0 ? (
        <EmptyCard
          icon={UsersIcon}
          title={hasSearched ? 'No journalists found' : 'Search the journalist database'}
          description={hasSearched
            ? 'Try adjusting your search query or filters to find more results.'
            : 'Enter at least three characters, or select a filter if you have full access.'}
          action={hasSearched ? (
            <Button variant="primary" onClick={handleClearFilters}>
              Clear Filters
            </Button>
          ) : null}
        />
      ) : (
        <ResultsGrid>
          {hits.map((hit, index) => (
            <ContactCard
              key={hit.objectID}
              contact={hitToContact(hit)}
              type="journalist"
              locked={!hasFullAccess}
              addToListComponent={<AddToListButton profile={hit} dataType="journalist" />}
              onAddToList={(contact) => {
                console.log('Add to list:', contact);
              }}
              onEmail={(contact) => {
                console.log('Email:', contact);
              }}
              style={{ animationDelay: `${index * 50}ms` }}
            />
          ))}
        </ResultsGrid>
      )}

      {hasPagination && hasSearched && hits.length > 0 &&
        (cursorIndex > 0 || hasMore) && (
        <div className="mt-8 flex items-center justify-center gap-4">
          <Button
            variant="secondary"
            onClick={handlePreviousPage}
            disabled={cursorIndex === 0 || isSearching}
          >
            Previous
          </Button>
          <span className="text-sm text-gray-600">Page {cursorIndex + 1}</span>
          <Button
            variant="secondary"
            onClick={handleNextPage}
            disabled={!hasMore || !nextCursor || isSearching}
          >
            Next
          </Button>
        </div>
      )}
    </DashboardLayout>
  );
}

export default JournalistDashboard;
